import type {
    ChildSelector,
    PropsChildren,
} from '../components/ui-element.type';
import type { UIElement } from '../components/index';
import { resolveChildDescriptor } from '../components/ui-element.helper';
import { GenericDecorator } from './decorator.type';
import { Mapped } from '../global/constants';

// Overloads for Child
export function Child(
    selector: ChildSelector
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Child(
    selector: ChildSelector,
    multiple: boolean
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Child(
    selector: ChildSelector,
    multiple: boolean,
    mapped: Mapped
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Child(
    selector: ChildSelector,
    mapped: Mapped
): GenericDecorator<void, ClassFieldDecoratorContext>;

/**
 * Defines a child element by selector or by PropsChildren.
 * @param {ChildSelector} selector - CSS selector for the child element
 * @param {boolean | Mapped} [multiple] - Whether to select multiple children or Mapped enum value
 */
export function Child(
    selector: ChildSelector,
    multiple?: boolean | Mapped
): GenericDecorator<void, ClassFieldDecoratorContext> {
    // Handle overload: Child(selector, Mapped)
    let actualMultiple = false;

    if (typeof multiple === 'object' || typeof multiple === 'number') {
        actualMultiple = false;
    } else if (typeof multiple === 'boolean') {
        actualMultiple = multiple;
    }

    return (_: void, context: ClassFieldDecoratorContext) => {
        if (context.kind === 'field') {
            context.addInitializer(function (this: unknown) {
                const instance = this as InstanceType<typeof UIElement>;
                const propertyName = String(context.name);
                const childDefinition: PropsChildren = {
                    selector,
                    multiple: actualMultiple,
                };

                const descriptor = resolveChildDescriptor(
                    propertyName,
                    childDefinition.selector,
                    actualMultiple
                );
                Object.defineProperty(instance, propertyName, descriptor);

                // This allows to keep backwards compatibility with legacy components,
                // which use `get static props() { ... }`
                const classConstructor =
                    instance.constructor as typeof UIElement;
                const ownDescriptor = Object.getOwnPropertyDescriptor(
                    classConstructor,
                    'props'
                );
                const isWritableDataProp =
                    ownDescriptor &&
                    !ownDescriptor.get &&
                    ownDescriptor.writable;
                if (!isWritableDataProp) {
                    const existingProps = classConstructor.props ?? {};
                    Object.defineProperty(classConstructor, 'props', {
                        value: {
                            ...existingProps,
                            children: { ...existingProps.children },
                        },
                        writable: true,
                        configurable: true,
                    });
                }
                classConstructor.props.children ??= {};
                classConstructor.props.children[propertyName] = childDefinition;
            });
        } else {
            throw new Error('@Child decorator is only applied to the property');
        }
    };
}

// Overloads for Children
export function Children(
    selector: ChildSelector
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Children(
    selector: ChildSelector,
    mapped: Mapped
): GenericDecorator<void, ClassFieldDecoratorContext>;

/**
 * Defines the children of the element by selector or by ChildSelector.
 * Same as Child, but selects multiple children.
 * @param {ChildSelector} selector - CSS selector for the child elements
 * @param {Mapped} [mapped] - Optional Mapped enum value to mark children as mapped
 */
export function Children(
    selector: ChildSelector,
    mapped?: Mapped
): GenericDecorator<void, ClassFieldDecoratorContext> {
    if (mapped !== undefined) {
        return Child(selector, true, mapped);
    }
    return Child(selector, true);
}
